import { openDisconnectPagePanel } from './delete-dynamic-page/openDisconnectPagePanel'
import {
  getRouterCollectionIdsFromNamespace,
  getFirstRouterCollectionId,
} from '../worker/utils/routerCollections'
import openAddDynamicPagePanel from './openAddDynamicPagePanel'
import { isWriteAllowed } from '@wix/wix-data-client-common/dist/esm/business-logic/collections/schemaQueries'
import { updatePageLinkFieldTitle } from '../business-logic/dynamic-pages/updatePageLinkFieldTitle'
import { getPatternDefForPage } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/router-config/routerConfig'
import { getCollection } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/router-config/patternDef'
import openDpErrorPanelForRemovedCollection from './openDpErrorPanelForRemovedCollection'
import openDpErrorPanelForRemovedDbDriver from './openDpErrorPanelForRemovedDbDriver'
import { ensureDataModeIsEnabled } from './enableDataMode'
import { createDPErrorHandler } from './utils/createDPErrorHandler'
import { getIsCollectionDeleted } from './utils/isCollectionDeleted'

import {
  UNDO_REDO_LABELS,
  resolveHasDpVariants,
  getDpVariantsCount,
  resolveIsDpVariant,
  isEditor3OrStudio2,
} from '@wix/wix-data-client-common'
import type {
  AppApi,
  DynamicPagesPlatformEvent,
  EDITOR_TYPE,
} from '@wix/wix-data-client-common'
import type {
  DataSettingsManager,
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
  RouterConfig,
  SafeUserPreferences,
} from '@wix/wix-data-client-common-standalone'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { PageRef, PlatformEvent } from '@wix/editor-platform-sdk-types'
import { getPageRouteData } from './utils/getPageRouteData'
import { openDpVariantsRemoveConnectionsWarning } from './openDpVariantsRemoveConnectionsWarning'

const isDbDriver = (collectionId: string) => collectionId.includes('/')

const getNamespace = (collectionId: string) => collectionId.split('/')[0]

const getCollectionIdOrThrow = (
  collectionId?: string,
  groupId?: string,
): string => {
  const result = collectionId || groupId
  if (!result) {
    throw new Error('Either collectionId or groupId must be provided')
  }
  return result
}

type Params = {
  appApi: AppApi
  logger: Logger
  editorSdkProxy: EditorSDK
  i18n: I18n
  collectionsApi: CollectionsApi
  dataSettingsManager: DataSettingsManager
  safeUserPreferences: SafeUserPreferences
  fedopsLogger: FedopsLogger
  editorType: EDITOR_TYPE
}

export const eventCreator = async ({
  appApi,
  logger,
  editorSdkProxy,
  i18n,
  collectionsApi,
  dataSettingsManager,
  safeUserPreferences,
  fedopsLogger,
  editorType,
}: Params) => {
  const isSiteWithoutCollections = async function () {
    const collections = await collectionsApi.getAll()
    return collections.length === 0
  }

  const openAutoCMSForDynamicPage = async ({
    pageRef,
    newItem = false,
    origin,
  }: {
    pageRef: PageRef
    newItem?: boolean
    origin: string
  }) => {
    const routerRef = await editorSdkProxy.document.routers.getByPage({
      pageRef,
    })
    const router = await editorSdkProxy.routers.get({ routerRef })
    const { def: patternDef } = getPatternDefForPage(
      router.config,
      router.pages,
      pageRef,
    )
    const collectionId = getCollection(patternDef)

    if (newItem) {
      appApi.openAutoCmsNewItemView({
        collectionId,
        origin,
      })
    } else {
      appApi.openAutoCmsCollectionView({
        collectionId,
        origin,
      })
    }
  }

  const isCollectionDeleted = getIsCollectionDeleted(collectionsApi)

  return async function (event: DynamicPagesPlatformEvent | PlatformEvent) {
    const { eventType, eventPayload } = event

    const withDPErrorHandler = createDPErrorHandler({
      i18n,
      logger,
      editorSdkProxy,
    })

    switch (eventType) {
      case 'addDpVariantClicked': {
        const routerRef = await editorSdkProxy.document.routers.getByPage({
          pageRef: eventPayload.pageRef,
        })
        const routerData = await editorSdkProxy.routers.get<RouterConfig>({
          routerRef,
        })
        const variantCount = getDpVariantsCount({
          pageRef: eventPayload.pageRef,
          routerData,
        })
        appApi.emit('addDpVariantClicked', {
          ...eventPayload,
          routerRef,
          variantCount,
        })
        break
      }

      case 'duplicateDpVariantClicked': {
        const routerRef = await editorSdkProxy.document.routers.getByPage({
          pageRef: eventPayload.pageRef,
        })
        const router = await editorSdkProxy.routers.get<RouterConfig>({
          routerRef,
        })
        appApi.emit('duplicateDpVariantClicked', {
          ...eventPayload,
          routerRef,
          router,
        })
        break
      }

      case 'editDpVariantClicked': {
        const routerRef = await editorSdkProxy.document.routers.getByPage({
          pageRef: eventPayload.pageRef,
        })
        const router = await editorSdkProxy.routers.get<RouterConfig>({
          routerRef,
        })
        appApi.emit('editDpVariantClicked', {
          ...eventPayload,
          routerRef,
          router,
          biOrigin: 'site_pages_panel',
        })
        break
      }

      case 'openAddPreset':
        await withDPErrorHandler(async () => {
          const { enableDataModeTriggered } = await ensureDataModeIsEnabled({
            dataSettingsManager,
            safeUserPreferences,
            logger,
          })

          const isFirstTimeExperience = enableDataModeTriggered

          appApi.openAddDynamicPagePanelV4({
            origin: eventPayload.origin,
            isFirstTimeExperience,
          })
        })

        break

      case 'openNewDynamicPagePanel':
        await withDPErrorHandler(async () => {
          const { enableDataModeTriggered } = await ensureDataModeIsEnabled({
            dataSettingsManager,
            safeUserPreferences,
            logger,
          })

          const isFirstTimeExperience = enableDataModeTriggered

          const collectionId =
            eventPayload.collectionId ||
            eventPayload.groupId ||
            (eventPayload.routerRef
              ? await getFirstRouterCollectionId({
                  editorSdkProxy,
                  routerRef: eventPayload.routerRef,
                })
              : undefined)

          await appApi.openAddDynamicPagePanelV5({
            origin: eventPayload.origin,
            collectionId,
            isFirstTimeExperience,
            canChangeCollection: true,
          })
        })

        break

      case 'openNewDynamicPageIntroPanel':
        appApi.openNewDynamicPageIntroPanel({
          origin: eventPayload.origin,
        })
        break

      case 'addBlankDynamicPageClicked': {
        appApi.addBlankDynamicPage({
          collectionId: getCollectionIdOrThrow(
            eventPayload.collectionId,
            eventPayload.groupId,
          ),
          origin: eventPayload.origin,
          panelName: 'NEW_DYNAMIC_PAGE',
        })

        break
      }

      case 'addDefaultDynamicItemPage':
        appApi.addDefaultDynamicItemPage({
          collectionId: getCollectionIdOrThrow(
            eventPayload.collectionId,
            eventPayload.groupId,
          ),
          origin: eventPayload.origin,
        })

        break

      case 'addDefaultDynamicListPage':
        appApi.addDefaultDynamicListPage({
          collectionId: getCollectionIdOrThrow(
            eventPayload.collectionId,
            eventPayload.groupId,
          ),
          origin: eventPayload.origin,
        })

        break

      case 'addDynamicPageClicked': {
        const { routerRef, pageRef, origin } = eventPayload
        let collectionId: string | undefined
        let namespace: string | undefined

        if (routerRef) {
          const firstRouterCollectionId = await getFirstRouterCollectionId({
            editorSdkProxy,
            routerRef,
          })
          const collectionIsDeleted = await isCollectionDeleted(
            firstRouterCollectionId,
          )

          if (isDbDriver(firstRouterCollectionId)) {
            namespace = getNamespace(firstRouterCollectionId)

            if (collectionIsDeleted) {
              return openDpErrorPanelForRemovedDbDriver({
                editorSdkProxy,
                i18n,
                logger,
                namespace,
              })
            }
          } else {
            collectionId = firstRouterCollectionId

            if (collectionIsDeleted) {
              return openDpErrorPanelForRemovedCollection({
                editorSdkProxy,
                i18n,
                logger,
                collectionId: firstRouterCollectionId,
              })
            }
          }
        } else {
          collectionId = eventPayload.collectionId || eventPayload.groupId
        }

        const introOrigins = [
          'pagesPanel',
          'pagesPanel/QuickAddMenu',
          'panel/pages',
        ]
        const originatesFromDynamicPagesIntro =
          introOrigins.includes(origin) && !collectionId

        await withDPErrorHandler(async () => {
          const { enableDataModeTriggered } = await ensureDataModeIsEnabled({
            dataSettingsManager,
            safeUserPreferences,
            logger,
          })

          const isFirstTimeExperience = enableDataModeTriggered

          await openAddDynamicPagePanel({
            origin,
            pageRef,
            namespace,
            collectionId,
            editorSdkProxy,
            appApi,
            collectionsApi,
            logger,
            i18n,
            originatesFromDynamicPagesIntro,
            isSiteWithoutCollections,
            isFirstTimeExperience,
          })
        })

        break
      }

      case 'deleteDynamicPageClicked': {
        const { pageRef, origin } = eventPayload
        appApi.openDeleteDynamicPagePanel({ pageRef, origin })
        break
      }

      case 'disconnectPageFromRouterClicked': {
        const { pageRef } = eventPayload
        const [canAddStaticPages, hasDpVariants] = await Promise.all([
          editorSdkProxy.editor.canAddStaticPage(),
          resolveHasDpVariants({ pageRef, editorSdkProxy }),
        ])

        if (!canAddStaticPages) {
          editorSdkProxy.editor.openConfirmationPanel({
            headerText: i18n.t(
              'Dynamic_Pages_Remove_Connections_100_Pages_Warning_Title',
            ),
            descriptionText: i18n.t(
              'Dynamic_Pages_Remove_Connections_100_Pages_Warning_Description',
            ),
            mainActionText: i18n.t(
              'Dynamic_Pages_Remove_Connections_100_Pages_Warning_Button_Text',
            ),
            shouldShowIllustration: true,
          })
          break
        }

        if (hasDpVariants) {
          await openDpVariantsRemoveConnectionsWarning({
            logger,
            editorSdkProxy,
            i18n,
            collectionsApi,
            pageRef,
          })
          break
        }

        await openDisconnectPagePanel(
          {
            editorSdkProxy,
            collectionsApi,
            logger,
            appApi,
            i18n,
            fedopsLogger,
            isCollectionDeleted,
          },
          eventPayload,
        )
        break
      }

      case 'removeRouterClicked': {
        editorSdkProxy.routers
          .remove({ routerRef: eventPayload.routerRef })
          .catch(error => {
            logger.error(new Error('Failed to remove router', { cause: error }))
          })

        break
      }

      case 'pageRenamed': {
        const { title, pageRef } = eventPayload

        const [{ pattern, definition, routerData }, isDpVariant] =
          await Promise.all([
            getPageRouteData({
              editorSdkProxy,
              pageRef,
            }),
            resolveIsDpVariant({
              pageRef,
              editorSdkProxy,
            }),
          ])

        if (isEditor3OrStudio2(editorType)) {
          await appApi.handleChangedRouters({ pageRefs: [pageRef] })
        } else {
          if (!isDpVariant) {
            updatePageLinkFieldTitle({
              routerPrefix: routerData.prefix,
              pattern,
              patternDefinition: definition,
              pageTitle: title,
              collectionsApi,
            })
          }
        }

        break
      }

      case 'addNewItemClicked':
        openAutoCMSForDynamicPage({
          newItem: true,
          ...eventPayload,
        })
        break

      case 'manageContentClicked': {
        const { pageRef, newItem, origin } = eventPayload
        openAutoCMSForDynamicPage({
          pageRef,
          newItem,
          origin,
        })
        break
      }

      case 'duplicateDynamicPage':
        await appApi.duplicateDynamicPage({
          pageRef: eventPayload.pageRef,
          origin: eventPayload.origin,
        })
        break

      case 'addNewRouterItemClicked': {
        const { routerRef, origin } = eventPayload
        const collectionId = await getFirstRouterCollectionId({
          editorSdkProxy,
          routerRef,
        })

        if (isDbDriver(collectionId)) {
          const collectionIdsForNamespace =
            await getRouterCollectionIdsFromNamespace({
              editorSdkProxy,
              namespace: getNamespace(collectionId),
            })

          if (collectionIdsForNamespace.length > 1) {
            await appApi.openAutoCmsCollectionsList({ origin })
            break
          }
        }

        const collectionIsDeleted = await isCollectionDeleted(collectionId)
        if (collectionIsDeleted) {
          await appApi.openAutoCmsCollectionView({
            collectionId,
            origin,
          })
          break
        }

        const schema = await collectionsApi.get(collectionId)
        if (isWriteAllowed(schema)) {
          await appApi.openAutoCmsNewItemView({
            collectionId,
            origin,
          })
        } else {
          await appApi.openAutoCmsCollectionView({
            collectionId,
            origin,
          })
        }

        break
      }

      case 'manageCollectionClicked': {
        const { routerRef, origin, groupId } = eventPayload
        const collectionId =
          groupId ||
          (await getFirstRouterCollectionId({
            editorSdkProxy,
            routerRef,
          }))

        if (isDbDriver(collectionId)) {
          const collectionIdsForNamespace =
            await getRouterCollectionIdsFromNamespace({
              editorSdkProxy,
              namespace: getNamespace(collectionId),
            })

          if (collectionIdsForNamespace.length > 1) {
            await appApi.openAutoCmsCollectionsList({ origin })
            break
          }
        }

        await appApi.openAutoCmsCollectionView({
          collectionId,
          origin,
        })

        break
      }

      case 'openContentManager': {
        const { groupId, origin } = eventPayload

        await appApi.openAutoCmsCollectionView({
          collectionId: groupId,
          origin,
        })

        break
      }

      case 'openPresetScanningPanel': {
        await appApi.openPresetScanningPanel({
          pageRef: eventPayload.pageRef,
        })

        break
      }

      case 'navBarMainActionClicked':
        if (eventPayload.actionId === 'openContentManagerInTableView') {
          try {
            await appApi.openAutoCmsItemViewForCurrentDynamicPage({
              origin: 'dynamicPageNavBarMainAction',
            })
          } catch (error: unknown) {
            logger.error(
              new Error(
                'Failed to open content manager for current dynamic page from nav bar main action',
                { cause: error },
              ),
              { actionId: eventPayload.actionId },
            )
          }
        }
        break

      case 'undo':
      case 'redo': {
        const { label, params } = eventPayload

        if (label === UNDO_REDO_LABELS.UPDATE_ROUTERS) {
          await appApi.handleChangedRouters({ pageRefs: params.pageRefs })
        }
        break
      }
    }
  }
}
